// @ts-nocheck
import produce from 'immer'
import arrayMove from 'array-move'
import update from 'immutability-helper'
import { each, omit, cloneDeep, isUndefined } from 'lodash'
import { not, AnyObject, stripSecretsFromBotApiUrl } from '@feedyou/utils'

import { StepSettingsSubset } from '../constants/stepSettings'
import { unsetDialogs } from '../components/steps/utils/stepServices/stepServices.js'
import * as actionTypes from '../constants/actionTypes'
import { getStepReducerByType } from '../components/steps/StepReducers'
import { diffPatcher } from '../store/socketServer/diffService'
import { StepSchema } from '@feedyou-ai/feedbot-schema'
import { createInitialToolForGivenType } from '../components/steps/Ai/utils'

const initialState = undefined

export function botFlowReducer(state = initialState, action) {
  switch (action.type) {
    case actionTypes.DISABLE_STEP_NLP_IN_STEP_TYPE_DEFAULTS:
      return produce(state, draft => {
        if (draft.stepTypeDefaults.question?.hasOwnProperty('fuzzyAnswers')) {
          delete draft.stepTypeDefaults.question.fuzzyAnswers
        }
      })

    case actionTypes.ADD_DIALOGS:
      return update(state, {
        dialogs: { $merge: action.payload }
      })

    case actionTypes.DELETE_DIALOGS: {
      const dialogs = {}
      const mainDialogs = []
      const dialogIds = action.payload || []
      const isMainDialogIdInGivenDialogIds = mainDialog =>
        dialogIds.some(dialogId => dialogId === mainDialog.id)

      const stateMainDialogs = state.mainDialogs ?? []

      mainDialogs.push(...stateMainDialogs.filter(not(isMainDialogIdInGivenDialogIds)))
      Object.entries(state.dialogs).forEach(([dialogId, dialog]) => {
        if (!action.payload.includes(dialogId)) {
          dialogs[dialogId] = {
            ...dialog,
            steps: dialog.steps?.map(step => unsetDialogs(step, dialogIds))
          }
        }
      })

      return { ...state, dialogs, mainDialogs }
    }

    case actionTypes.RENAME_DIALOG: {
      const { id, name } = action.payload

      if (!state.dialogs[id]) {
        return update(state, {
          dialogs: { [id]: { $set: { id, name, steps: [] } } }
        })
      }

      return update(state, {
        dialogs: { [id]: { name: { $set: name } } }
      })
    }

    case actionTypes.MOVE_STEP: {
      let { srcDialogId, destDialogId, srcIndex, destIndex } = action.payload
      const item = state.dialogs[srcDialogId].steps[srcIndex]

      if (srcDialogId === destDialogId) {
        if (srcIndex < destIndex) {
          destIndex--
        }

        return update(state, {
          dialogs: {
            [srcDialogId]: {
              steps: steps => arrayMove(steps, srcIndex, destIndex)
            }
          }
        })
      } else {
        return update(state, {
          dialogs: {
            [srcDialogId]: {
              steps: { $splice: [[srcIndex, 1]] }
            },
            [destDialogId]: dialog =>
              update(dialog || {}, {
                id: { $set: destDialogId },
                steps: steps => update(steps || [], { $splice: [[destIndex, 0, item]] })
              })
          }
        })
      }
    }

    case actionTypes.UPDATE_STEP_PROPERTIES: {
      const { stepId, parentDialogId, ...rest } = action.payload
      return {
        ...state,
        dialogs: {
          ...state.dialogs,
          [parentDialogId]: {
            ...state.dialogs[parentDialogId],
            steps: state.dialogs[parentDialogId].steps.map(step =>
              step.id === stepId ? updateStepOrSubstep({ step, ...rest }) : step
            )
          }
        }
      }
    }

    case actionTypes.REPLACE_STEP: {
      const { parentDialogId, stepId, step } = action.payload

      return produce(state, draft => {
        const steps = draft.dialogs[parentDialogId].steps
        const stepIndex = steps?.findIndex(step => step.id === stepId)

        if (stepIndex !== -1) {
          steps[stepIndex] = step
        }
      })
    }

    case actionTypes.MOVE_ANSWER: {
      const { srcDialogId, destDialogId, srcStepId, destStepId, srcIndex } = action.payload

      let destIndex = action.payload.destIndex

      if (srcStepId === destStepId) {
        if (srcIndex < destIndex) {
          --destIndex
        }

        return update(state, {
          dialogs: {
            [srcDialogId]: {
              steps: steps =>
                steps.map(step =>
                  step.id === srcStepId
                    ? { ...step, answers: arrayMove(step.answers, srcIndex, destIndex) }
                    : step
                )
            }
          }
        })
      } else if (srcDialogId === destDialogId) {
        let answerToMove = state.dialogs[srcDialogId].steps.find(({ id }) => id === srcStepId)
          .answers[srcIndex]

        return update(state, {
          dialogs: {
            [srcDialogId]: {
              steps: steps =>
                steps.map(step => {
                  switch (step.id) {
                    case srcStepId:
                      return update(step, {
                        answers: { $splice: [[srcIndex, 1]] }
                      })

                    case destStepId:
                      return update(step, {
                        answers: { $splice: [[destIndex, 0, answerToMove]] }
                      })

                    default:
                      return step
                  }
                })
            }
          }
        })
      } else {
        let answerToMove = state.dialogs[srcDialogId].steps.find(({ id }) => id === srcStepId)
          .answers[srcIndex]

        return update(state, {
          dialogs: {
            [srcDialogId]: {
              steps: steps =>
                steps.map(step =>
                  step.id === srcStepId
                    ? update(step, {
                        answers: { $splice: [[srcIndex, 1]] }
                      })
                    : step
                )
            },
            [destDialogId]: {
              steps: steps =>
                steps.map(step =>
                  step.id === destStepId
                    ? update(step, { answers: { $splice: [[destIndex, 0, answerToMove]] } })
                    : step
                )
            }
          }
        })
      }
    }

    case actionTypes.DELETE_DIALOG_OCCURENCES:
      return produce(state, draft => {
        const { dialogId } = action

        const doesStepLinkTargetDialog = step => step.type === 'dialog' && step.dialog === dialogId

        each(draft.dialogs, dialogDraft => {
          const filteredSteps = dialogDraft.steps.filter(not(doesStepLinkTargetDialog))

          // Only mutate steps when they actually change to keep original ref when they don't
          if (filteredSteps.length < dialogDraft.steps.length) {
            dialogDraft.steps = filteredSteps
          }
        })
      })

    case actionTypes.UPDATE_BOT_PROPERTIES:
    case actionTypes.SAVE_BOT_SUCCESS:
      return update(state, {
        $merge: action.payload
      })

    case actionTypes.DELETE_STEP: {
      return update(state, {
        dialogs: {
          [action.dialogId]: {
            steps: steps => steps.filter((_, index) => index !== action.stepIndex)
          }
        }
      })
    }

    case actionTypes.ADD_STEP: {
      const { dialogId, step, index } = action

      return update(state, {
        dialogs: {
          [dialogId]: dialog =>
            update(dialog || { id: dialogId, steps: [] }, {
              steps: { $splice: [[index, 0, step]] }
            })
        }
      })
    }

    case actionTypes.STEP_ACTION: {
      const { stepAction, dialogId, stepIndex, selectedMutations } = action
      const { dialogs } = state

      return {
        ...state,
        dialogs: {
          ...dialogs,
          [dialogId]: {
            ...dialogs[dialogId],
            steps: dialogs[dialogId].steps.map((step, index) => {
              if (index !== stepIndex) {
                return step
              }

              const stepReducer = getStepReducerByType(step.type)
              return stepReducer(
                step,
                stepAction,
                Object.keys(selectedMutations).length > 0 ? selectedMutations : null
              )
            })
          }
        }
      }
    }

    case actionTypes.APPLY_DIFF: {
      const { diff, originalVersion } = action.payload
      if (!diff) {
        return originalVersion
      }

      try {
        return diffPatcher.patch(cloneDeep(originalVersion), JSON.parse(diff))
      } catch (err) {
        console.error('Diff patching failed', err)
        return originalVersion
      }
    }

    case actionTypes.DISCARD_CHANGES: {
      return action.payload
    }

    case actionTypes.STRIP_API_KEY_FROM_API_URL: {
      return produce(state, draft => {
        if (!draft?.apiUrl) {
          return draft
        }

        draft.apiUrl = stripSecretsFromBotApiUrl(draft.apiUrl)
      })
    }

    case actionTypes.STRIP_SDK_VERSION_FROM_BOT: {
      return produce(state, draft => {
        if (!draft?.sdkVersion) {
          return draft
        }

        delete draft.sdkVersion
      })
    }

    default:
      return state
  }
}

function updateStepOrSubstep(params: {
  step: StepSchema
  subset: StepSettingsSubset
  formData: AnyObject
  substepId?: string
  substepIndex?: number
}) {
  switch (params.subset) {
    case StepSettingsSubset.CASE: {
      const step = cloneDeep(params.step)
      const caseIndex = step.cases.findIndex(({ id }) => id === params.substepId)
      if (caseIndex >= 0) {
        step.cases[caseIndex] = omit(params.formData, 'type')
      }
      return step
    }
    case StepSettingsSubset.ANSWER: {
      const step = cloneDeep(params.step)
      const answerIndex = step.answers.findIndex(({ id }) => id === params.substepId)
      if (answerIndex >= 0) {
        step.answers[answerIndex] = omit(params.formData, 'type')
      }
      return step
    }
    case StepSettingsSubset.TOOL: {
      const step = cloneDeep(params.step)
      const toolIndex = step.tools?.findIndex(({ id }) => id === params.substepId)
      if (toolIndex >= 0) {
        step.tools[toolIndex] = omit(params.formData, 'type')
      }

      if (toolIndex < 0 && params.substepIndex >= 0) {
        step.tools[params.substepIndex] = omit(params.formData, 'type')
      }

      if (!step.tools?.length || (toolIndex < 0 && isUndefined(params.substepIndex))) {
        const tools = step.tools || []
        const newTool = createInitialToolForGivenType(params.formData.action?.type)
        return { ...step, tools: [...tools, { ...newTool, ...omit(params.formData, 'type') }] }
      }

      return step
    }
    case StepSettingsSubset.MESSAGE_CAROUSEL: {
      const step = cloneDeep(params.step)
      const carouselIndex = step.carousel.findIndex(({ id }) => id === params.substepId)
      if (carouselIndex >= 0) {
        step.carousel[carouselIndex] = omit(params.formData, 'type')
      }
      return step
    }
    case StepSettingsSubset.DEFAULT_CASE: {
      const { step, formData } = params
      return { ...step, default: { ...step.default, ...omit(formData, 'type') } }
    }
    case StepSettingsSubset.DEFAULT_ANSWER: {
      const { step, formData } = params
      return { ...step, defaultAnswer: { ...step.defaultAnswer, ...omit(formData, 'type') } }
    }
    case StepSettingsSubset.ANSWER_ITEM_MUTATIONS: {
      const step = cloneDeep(params.step)
      const answerIndex = step.answers.findIndex(({ id }) => id === params.substepId)
      if (answerIndex >= 0) {
        step.answers[answerIndex].mutations = params.formData.mutations
      }
      return step
    }
    case StepSettingsSubset.MESSAGE_CAROUSEL_ITEM_MUTATIONS: {
      const step = cloneDeep(params.step)
      const carouselIndex = step.carousel.findIndex(({ id }) => id === params.substepId)
      if (carouselIndex >= 0) {
        step.carousel[carouselIndex].mutations = params.formData.mutations
      }
      return step
    }
    default:
      return { ...params.step, ...params.formData }
  }
}
